import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { cureException } from '../../api/originatorApi';

/**
 * Cure-notes modal. Replaces the old window.prompt flow. Calls the EXISTING
 * cureException(exceptionId, cure_notes) contract — no API change.
 *
 * props: { exception, onClose, onCured }
 */
export function CureExceptionModal({ exception, onClose, onCured }) {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setNotes('');
    setError('');
    setSaving(false);
  }, [exception]);

  const open = Boolean(exception);

  async function handleSubmit() {
    const trimmed = notes.trim();
    if (!trimmed) {
      setError('Cure notes are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await cureException(exception.id, trimmed);
      // Pass the cured record back so callers can update in place without a
      // dedicated single-exception GET (see build-ledger wiring TODO).
      onCured?.(result);
    } catch (requestError) {
      setError(requestError.message || 'Unable to cure exception.');
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      title="Cure exception"
      description={
        exception
          ? `${exception.code} · package ${exception.package_id}`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="gradient" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Submit cure'}
          </Button>
        </>
      }
    >
      <label className="block text-[12px] font-semibold text-eorig-ink">
        Cure notes
      </label>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
        autoFocus
        placeholder="Describe how the exception was resolved…"
        className="mt-1.5 w-full rounded-xl border border-eorig-border bg-white px-3 py-2 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
      />
      {error ? (
        <p className="mt-2 text-[12px] font-medium text-rose-600">{error}</p>
      ) : null}
    </Modal>
  );
}
